import React, { ReactNode } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface SimpleFormProps {
  methods: UseFormReturn<any>;
  onSubmit: (data: any) => void;
  children: ReactNode;
  submitText?: string;
  cancelText?: string;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  className?: string;
  footerClassName?: string;
  showFooter?: boolean;
}

export const SimpleForm: React.FC<SimpleFormProps> = ({
  methods,
  onSubmit,
  children,
  submitText = 'Submit',
  cancelText = 'Cancel',
  onCancel,
  isSubmitting = false,
  isDisabled = false,
  className = '',
  footerClassName = '',
  showFooter = true,
}) => {
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    methods.handleSubmit(onSubmit)(e);
  };

  return (
    <FormProvider {...methods}>
      <div className={cn('flex flex-col h-full', className)}>
        <div className="flex-1 overflow-y-auto space-y-6 p-1">{children}</div>

        {showFooter && (
          <DialogFooter
            className={cn('pt-4 mt-auto border-t', footerClassName)}
          >
            <div className="flex gap-2 w-full sm:w-auto">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isDisabled || isSubmitting}
                  className="flex-1 sm:flex-initial"
                >
                  {cancelText}
                </Button>
              )}
              <Button
                type="button"
                onClick={handleFormSubmit}
                disabled={isDisabled || isSubmitting}
                className="flex-1 sm:flex-initial bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  submitText
                )}
              </Button>
            </div>
          </DialogFooter>
        )}
      </div>
    </FormProvider>
  );
};
